import React, { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { get } from 'object-path';
import { DialogWithBody } from 'MoshtixShared/component-dialog/src/dialog-with-body';
import TermsDialog from 'MoshtixShared/component-terms-dialog';
import { A, Div, H3, Img, P } from 'MoshtixShared/component-element';
import tokenStore, { setAccessToken } from 'MoshtixShared/helper-token-store';
import { Button } from 'MoshtixShared/component-button';
import { TextBox } from 'MoshtixShared/component-text-box';
import { Icon } from 'MoshtixShared/component-icon';

import { TicketInterface } from 'My/types/app';
import appConfiguration from '../../../configuration';
import {
  dialogStyles,
  headerCss,
  noteCss,
  noteCssIcon,
  assignTermsCss,
  shareButtonWrapperCss,
  textBoxCss,
  termsLinkCss,
} from './style';

import assignIcon from './Assign-icon.png';
import { useMutation } from '../../../hooks/helper-apollo-hooks';
import { CREATE_INVITATION_MUTATION } from './data-definition';

export type AssignTicketDialogProps = {
  onClose?: () => void;
  ticket: TicketInterface;
};

const RESALE_HELP_URL = 'https://tixsupport.moshtix.com.au/hc/en-us/articles/360000518676-What-is-Resale';

export const AssignTicketDialog: FC<AssignTicketDialogProps> = ({ onClose: onCloseClick, ticket }) => {
  const [showAssignTermsDialog, setShowAssignTermsDialog] = useState(false);

  const [createInvitation, { data, error, loading }] = useMutation(CREATE_INVITATION_MUTATION, {
    variables: {
      token: tokenStore.getTicketDistroToken(),
    },
  });

  const inviteAccessToken = useMemo(
    () => get(ticket, 'ticketAssign.accessToken', null) || get(data, 'viewer.invitationAccessKey', null),
    [ticket, data],
  );

  useEffect(() => {
    if (ticket.id && !inviteAccessToken) {
      createInvitation({
        variables: {
          ticketId: ticket.id,
        },
      });
    }
  }, [ticket.id, inviteAccessToken, createInvitation]);

  const url = useMemo(
    () =>
      loading || !inviteAccessToken
        ? 'Getting the link...'
        : `https://${appConfiguration.myUrl}/ticket-accept/${inviteAccessToken}/${ticket.ticketKey}`,
    [ticket, loading, inviteAccessToken],
  );

  const copyUrlToClipboard = useCallback(async (ev) => {
    ev.preventDefault();
    await navigator.clipboard.writeText(ev.currentTarget.value);
  }, []);

  const shareUrl = useCallback(async () => {
    try {
      // the sharing action when cancelled (user clicks away, PE)
      // the browser will throw an error on the screen if not caught
      await navigator.share({ url });
      // eslint-disable-next-line no-empty
    } catch {}
  }, [url]);

  const handleTermsClick = useCallback((ev) => {
    ev.preventDefault();
    setShowAssignTermsDialog(true);
  }, []);

  return (
    <>
      <DialogWithBody
        headerCss={headerCss}
        iconLeft={<Img src={assignIcon} />}
        closeWithEsc
        id="assign-ticket"
        show
        title="Assign ticket"
        onCloseClick={onCloseClick}
        hideButtonContent={!navigator.share}
        buttonContent={
          <Div css={shareButtonWrapperCss}>
            <Button buttonType="tertiaryAlternate" iconLeft="upload" onClick={shareUrl}>
              SHARE
            </Button>
          </Div>
        }
      >
        <Div css={dialogStyles}>
          <H3>Assign ticket access to another person.</H3>
          <P>
            Copy or share the link to assign ticket access to another person. They will need to accept the invitation to
            access the ticket.
          </P>
          <Div css={noteCss}>
            <Icon css={noteCssIcon} type="exclamation" />
            <Div>
              <P>Assigning access to a ticket</P>
              <P>
                Assigning a ticket grants ticket access only to enter the event. You remain the ticket owner. Each
                ticket is <b>valid for 1 entry</b> and can be scanned only once. While assigned, the barcode is hidden
                and unavailable for your use.{' '}
              </P>
              <P>
                To sell this ticket, use{' '}
                <A href={RESALE_HELP_URL} css={assignTermsCss}>
                  Private Resale
                </A>
              </P>
              <P>
                <A href="#" onClick={handleTermsClick} css={termsLinkCss}>
                  TERMS &amp; CONDITIONS
                </A>
              </P>
            </Div>
          </Div>
          <TextBox
            value={url}
            iconRight="copy"
            iconRightLoading={loading || !inviteAccessToken}
            css={textBoxCss}
            readOnly
            onClick={copyUrlToClipboard}
          />
        </Div>
      </DialogWithBody>
      <TermsDialog
        show={showAssignTermsDialog}
        onClose={() => setShowAssignTermsDialog(false)}
        linkCss={assignTermsCss}
      />
    </>
  );
};
